import React, { useState, useEffect } from 'react';
import { Box, CardMedia, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Divider } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { primaryColor } from '../theme';
import '../App.css';
import { ArrowBack } from '@mui/icons-material';
import moment from 'moment';

const ContentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [deleteComment, setDeletionComment] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [comments, setComments] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content/${id}`);
        setContent(response.data);
        setComments(response.data.Comments || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const handleEdit = () => {
    navigate(`/content/${id}/edit`);
  };

  const handleDeleteOpen = () => {
    setDeleteOpen(true);
  };

  const handleAddComment = async () => {
    const user = JSON.parse(localStorage.getItem('user'));

    if (!user) {
      console.error('User not found in localStorage');
      return;
    }
    
    try {
      const newComment = { body: comment, date: new Date().toISOString(), userId: user.id };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/comments`, newComment);
      setComments([...comments, newComment]);
      setComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteChange = (e) => {
    setDeletionComment(e.target.value);
    if (e.target.value.trim()) {
      setDeleteError('');
    }
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteComment.trim()) {
      setDeleteError('Please enter deletion reason');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user ? user.id : null;
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/content/${id}`, {
        data: { userId, deleteComment }
      });
      setDeleteOpen(false);
      navigate('/editor/dashboard'); // Navigate back to the dashboard
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Content not found</Typography>
      </Container>
    );
  }

  const getPresentationEmbedUrl = (url) => {
    if (url.endsWith('.pptx')) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    return url;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/editor/dashboard')} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Typography variant="h4" gutterBottom style={{ color: primaryColor }}>
        Content Details
      </Typography>
      <Typography variant="h6" gutterBottom>{content.topic}</Typography>
      <Typography variant="subtitle1" gutterBottom>Target User: {content.targetUser}</Typography>
      <Typography variant="subtitle1" gutterBottom>Value Chain: {content.valueChain}</Typography>
      <Typography variant="subtitle1" gutterBottom>Platform: {content.platform}</Typography>
      {content.video && (
        <Box mt={2}>
          <CardMedia
            component="video"
            controls
            height="200"
            src={content.video}
            title={content.topic}
          />
        </Box>
      )}
      {content.audio && (
        <Box mt={2}>
          <audio controls>
            <source src={content.audio} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </Box>
      )}
      {content.presentation && (
        <Box mt={2}>
          <iframe
            src={getPresentationEmbedUrl(content.presentation)}
            width="100%"
            height="400"
            title={content.topic}
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      )}
      <Typography variant="body1" dangerouslySetInnerHTML={{ __html: content.content }} />
      <Box mt={2} display="flex" justifyContent="space-between">
        <Button variant="contained" color="primary" onClick={handleEdit} sx={{ backgroundColor: primaryColor }}>
          Edit
        </Button>
        <Button variant="contained" color="error" onClick={handleDeleteOpen}>
          Delete
        </Button>
      </Box>
      <Divider sx={{ my: 4 }} />
      <Box mt={2} mb={4}>
        <Typography variant="h6" gutterBottom>Comments</Typography>
        {comments.map((comment, index) => (
          <Box key={index} mb={2}>
            <Typography variant="body2">{comment.body}</Typography>
            <Typography variant="caption" color="textSecondary">
              {comment.User.firstName} ({comment.User.role}) - {moment(comment.date).format('YYYY-MM-DD HH:mm')}
            </Typography>
          </Box>
        ))}
        <TextField
          label="Add a comment"
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
        />
        <Button variant="contained" color="primary" onClick={handleAddComment} sx={{ mt: 2 }}>
          Add Comment
        </Button>
      </Box>

      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography mb={2}>Are you sure you want to delete this content?</Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Deletion Reason"
            type="text"
            rows={4}
            value={deleteComment}
            onChange={handleDeleteChange}
            fullWidth
            error={!!deleteError}
            helperText={deleteError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContentDetailPage;